import { Action } from "redux";
import { isType } from "typescript-fsa";

import { ICalendarSettingsState } from "../classes/state";
import { initialCalendarSettings } from "./initialState";

import * as types from "../actions/actionTypes";
import * as actions from "../actions/calendarSettingsActions";
import * as searchToolbarActions from "../actions/searchToolbarActions";

import * as dates from "./changeTimePeriod";
import * as viewModes from "./viewModes";

export default function calendarSettingsReducer(state: ICalendarSettingsState = initialCalendarSettings, action: Action): ICalendarSettingsState {

  if (isType(action, searchToolbarActions.loadCalendarSettingsSuccess)) {
    const viewModeOptions = action.payload.viewModeOptions;
    const hasCreatePermission = action.payload.hasCreatePermission;
    const hasDeletePermission = action.payload.hasDeletePermission;
    const hasEditPermission = action.payload.hasEditPermission;
    const isTwoWayCalendar = action.payload.isTwoWayCalendar;
    const userDateFormat = action.payload.userDateFormat;
    const calendarName = action.payload.calendarName;
    const useModernAddAndEditEventScreen = action.payload.useModernAddAndEditEventScreen;
    const newState: ICalendarSettingsState = {
      ...state,
      viewModeOptions,
      hasCreatePermission,
      hasDeletePermission,
      hasEditPermission,
      isTwoWayCalendar,
      userDateFormat,
      calendarName,
      useModernAddAndEditEventScreen,
    };
    return newState;
  }

  if (isType(action, actions.setCalendarSettingsFromPortlet_ONLY)) {
    const currentDate = dates.parseDate(action.payload.currentDate);
    const selectedDate = currentDate;
    const todaysDate = dates.parseDate(action.payload.todaysDate);
    const viewMode = action.payload.viewMode.toUpperCase();
    const newState: ICalendarSettingsState = { ...state, ...action.payload, currentDate, selectedDate, todaysDate, viewMode };
    return newState;
  }

  if (isType(action, actions.updateToAndFromDates)) {
    const viewTimePeriod = getViewTimePeriod(new Date(state.selectedDate), state.firstDayOfWeek, state.viewMode);
    const currentDate = state.selectedDate;
    const newState: ICalendarSettingsState = { ...state, ...viewTimePeriod, currentDate };
    return newState;
  }

  if (isType(action, actions.setViewMode_ONLY)) {
    const viewMode = viewModes.ConvertToStandardViewMode(action.payload);
    const newState: ICalendarSettingsState = { ...state, viewMode };
    return newState;
  }

  if (isType(action, actions.setEventScreenMode_ONLY)) {
    const eventScreenMode = toEventScreenMode(action.payload);
    const newState: ICalendarSettingsState = { ...state, eventScreenMode };
    return newState;
  }

  if (isType(action, actions.setEventId_ONLY)) {
    const displayedEventId = action.payload;
    const newState: ICalendarSettingsState = { ...state, displayedEventId };
    return newState;
  }

  if (isType(action, actions.setiCalStartDate_ONLY)) {
    const iCalStartDate = action.payload;
    const newState: ICalendarSettingsState = { ...state, iCalStartDate };
    return newState;
  }

  if (isType(action, actions.setDisplayedEventLocalStartDate_ONLY)) {
    const displayedEventLocalStartDate = action.payload;
    const newState: ICalendarSettingsState = { ...state, displayedEventLocalStartDate };
    return newState;
  }

  if (isType(action, actions.setDisplayedEventLocalEndDate_ONLY)) {
    const displayedEventLocalEndDate = action.payload;
    const newState: ICalendarSettingsState = { ...state, displayedEventLocalEndDate };
    return newState;
  }

  if (isType(action, actions.setCurrentYear_ONLY)) {
    const newYear: number = action.payload;
    const newCurrentDate = dates.setCurrentYear(new Date(state.currentDate), newYear);
    return setNewCurrentDateState(state, newCurrentDate);
  }

  if (isType(action, actions.setCurrentMonth_ONLY)) {
    const newMonth: number = action.payload;
    const newCurrentDate = dates.setCurrentMonth(new Date(state.currentDate), newMonth);
    return setNewCurrentDateState(state, newCurrentDate);
  }

  if (isType(action, actions.setCurrentDateToToday_ONLY)) {
    const newCurrentDate: Date = new Date();
    return setNewCurrentDateState(state, newCurrentDate);
  }

  if (isType(action, actions.setCurrentDateToPreviousPeriod_ONLY)) {
    const newCurrentDate: Date = dates.changeTimePeriod(new Date(state.currentDate), state.viewMode, -1, state.firstDayOfWeek);
    return setNewCurrentDateState(state, newCurrentDate);
  }

  if (isType(action, actions.setCurrentDateToNextPeriod_ONLY)) {
    const newCurrentDate: Date = dates.changeTimePeriod(new Date(state.currentDate), state.viewMode, 1, state.firstDayOfWeek);
    return setNewCurrentDateState(state, newCurrentDate);
  }

  if (isType(action, actions.setCurrentDate_ONLY)) {
    const newCurrentDate = new Date(action.payload);
    return setNewCurrentDateState(state, newCurrentDate);
  }

  if (isType(action, actions.setSelectedDate_ONLY)) {
    const newSelectedDate = new Date(action.payload);
    return setNewSelectedDateState(state, newSelectedDate);
  }

  return state;
};

function setNewCurrentDateState(state: ICalendarSettingsState, newCurrentDate: Date): ICalendarSettingsState {

  const newState: ICalendarSettingsState = {
    ...state,
    currentDate: newCurrentDate,
    selectedDate: newCurrentDate,
  };
  return newState;
}

function setNewSelectedDateState(state: ICalendarSettingsState, newSelectedDate: Date): ICalendarSettingsState {

  const newState: ICalendarSettingsState = {
    ...state,
    selectedDate: newSelectedDate,
  };
  return newState;
}

function getViewTimePeriod(selectedDate: Date, firstDayOfWeek: string, viewMode: string): any {

  let localFromDate = new Date();
  let localToDate = new Date();

  switch (viewMode) {
    case "DAILY":
    case "DAILYLIST": {
      localFromDate = dates.getFirstDateInCurrentDay(selectedDate);
      localToDate = dates.getLastDateInCurrentDay(selectedDate);
      break;
    }
    case "WEEKLY": {
      localFromDate = dates.getFirstDateInCurrentWeek(selectedDate, firstDayOfWeek);
      localToDate = dates.getLastDateInCurrentWeek(selectedDate, firstDayOfWeek);
      break;
    }
    case "MONTHLY": {
      localFromDate = dates.getFirstDateInCurrentMonth(selectedDate, firstDayOfWeek, true);
      localToDate = dates.getLastDateInCurrentMonth(selectedDate, firstDayOfWeek, true);
      break;
    }
    case "MONTHLYLIST": {
      localFromDate = dates.getFirstDateInCurrentMonth(selectedDate, firstDayOfWeek, false);
      localToDate = dates.getLastDateInCurrentMonth(selectedDate, firstDayOfWeek, false);
      break;
    }
    default: {
      break;
    }
  }
  return { localFromDate, localToDate };
}

const toEventScreenMode = (mode: string): "LIST" | "VIEW" | "EDIT" | "ADD" => {
  if (mode === "LIST" || mode === "VIEW" || mode === "EDIT" || mode === "ADD") { return mode; }
  return "LIST";
};
